import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Panel } from "./saUi";

const Toggle = ({ on, onChange, label, hint, testid }) => (
  <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
    <div><p className="font-bold text-sm">{label}</p>{hint && <p className="text-xs text-slate-400">{hint}</p>}</div>
    <button type="button" onClick={() => onChange(!on)} data-testid={testid}
      className={`w-11 h-6 rounded-full relative transition-colors ${on ? "bg-emerald-500" : "bg-slate-300"}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? "left-5" : "left-0.5"}`} />
    </button>
  </div>
);

const Num = ({ label, value, onChange, testid, suffix }) => (
  <label className="block">
    <span className="block text-xs text-slate-500 mb-1">{label}</span>
    <div className="flex items-center gap-2">
      <input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500" />
      {suffix && <span className="text-xs text-slate-400 whitespace-nowrap">{suffix}</span>}
    </div>
  </label>
);

export default function SaaSConfig() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/subscription-config").then((r) => setC(r.data)).catch(() => {}); }, []);

  const [resyncing, setResyncing] = useState(false);
  const resyncMembers = async () => {
    setResyncing(true);
    try {
      const { data } = await api.post("/admin/households/resync-members");
      toast.success(`Hersteld: ${data.roles_synced} rol(len), ${data.trials_restored} proef(en), ${data.bundle_members_synced} bundel-leden`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setResyncing(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...c,
        trial_days: Number(c.trial_days) || 0,
        trial_max_tasks: Number(c.trial_max_tasks) || 0,
        trial_max_recipes: Number(c.trial_max_recipes) || 0,
        trial_max_content: Number(c.trial_max_content) || 0,
        price_3m: Math.round(Number(c._e3 ?? c.price_3m / 100) * 100),
        price_6m: Math.round(Number(c._e6 ?? c.price_6m / 100) * 100),
        price_12m: Math.round(Number(c._e12 ?? c.price_12m / 100) * 100),
        price_family_3m: Math.round(Number(c._f3 ?? c.price_family_3m / 100) * 100),
        price_family_6m: Math.round(Number(c._f6 ?? c.price_family_6m / 100) * 100),
        price_family_12m: Math.round(Number(c._f12 ?? c.price_family_12m / 100) * 100),
        family_seats: Number(c.family_seats) || 6,
      };
      const { data } = await api.put("/admin/subscription-config", payload);
      setC(data);
      toast.success("Configuratie opgeslagen");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!c) return <div className="flex items-center gap-2 text-slate-400 py-12"><Loader2 className="w-5 h-5 animate-spin" /> Laden…</div>;
  const set = (k, v) => setC({ ...c, [k]: v });

  return (
    <div className="space-y-6 max-w-2xl" data-testid="sa-config">
      <Panel title="Paywall & AI">
        <div className="space-y-3">
          <Toggle on={c.paywall_enabled !== false} onChange={(v) => set("paywall_enabled", v)} testid="sa-cfg-paywall"
            label="Paywall actief" hint="Uit = alle gebruikers onbeperkt (preview)." />
          <Toggle on={!!c.trial_ai_enabled} onChange={(v) => set("trial_ai_enabled", v)} testid="sa-cfg-ai"
            label="AI tijdens proef" hint="Wizzy/AI beschikbaar in de proefperiode." />
        </div>
      </Panel>

      <Panel title="Proeflimieten">
        <div className="grid grid-cols-2 gap-4">
          <Num label="Proefduur" value={c.trial_days} onChange={(v) => set("trial_days", v)} testid="sa-cfg-trial-days" suffix="dagen" />
          <Num label="Max. taken" value={c.trial_max_tasks} onChange={(v) => set("trial_max_tasks", v)} testid="sa-cfg-max-tasks" />
          <Num label="Max. recepten" value={c.trial_max_recipes} onChange={(v) => set("trial_max_recipes", v)} testid="sa-cfg-max-recipes" />
          <Num label="Max. mededelingen & items" value={c.trial_max_content} onChange={(v) => set("trial_max_content", v)} testid="sa-cfg-max-content" />
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Items = alles uit "Meer": mededelingen, vieringen, pin dates, activiteiten, onderhoud en kennisbank-artikelen. Dit is het maximum dat een gebruiker tijdens de gratis proef kan aanmaken.</p>
      </Panel>

      <Panel title="Prijzen (eenmalig)">
        <div className="grid grid-cols-3 gap-4">
          <Num label="3 maanden" value={c._e3 ?? (c.price_3m / 100)} onChange={(v) => set("_e3", v)} testid="sa-cfg-price-3m" suffix="€" />
          <Num label="6 maanden" value={c._e6 ?? (c.price_6m / 100)} onChange={(v) => set("_e6", v)} testid="sa-cfg-price-6m" suffix="€" />
          <Num label="12 maanden" value={c._e12 ?? (c.price_12m / 100)} onChange={(v) => set("_e12", v)} testid="sa-cfg-price-12m" suffix="€" />
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Prijzen worden automatisch met Stripe gesynchroniseerd na opslaan.</p>
      </Panel>

      <Panel title="Onderhoud">
        <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
          <div>
            <p className="font-bold text-sm">Abonnementen herstellen</p>
            <p className="text-xs text-slate-400">Synct rol + abonnement van álle gebruikers die nog in een huishouden zitten. Herstelt o.a. verlopen proef na het verwijderen van een huishouden.</p>
          </div>
          <button type="button" onClick={resyncMembers} disabled={resyncing} data-testid="sa-cfg-resync"
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg px-4 py-2 font-head font-bold text-sm disabled:opacity-60 whitespace-nowrap">
            {resyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Herstel nu
          </button>
        </div>
      </Panel>

      <button onClick={save} disabled={busy} data-testid="sa-cfg-save"
        className="flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white rounded-lg px-5 py-2.5 font-head font-bold text-sm disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={2.5} />} Opslaan
      </button>
    </div>
  );
}
